import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Kogia | Terms of Service (Contributors)' },
]

export default function ContributorsTermsOfServiceRoute() {
	return (
		<div className="mx-auto flex max-w-screen-lg flex-col gap-12 p-4 pb-20 md:pt-10">
			<div className="flex flex-col gap-2">
				<h2 className="mb-4 font-serif text-h2">
					<strong className="font-bold">
						Terms of Service (Contributors): Image and Video Donation
					</strong>
				</h2>
				<p>
					<em>Last updated: Feb 1, 2026</em>
				</p>
				<p>
					Please read the following terms of service carefully. By submitting
					images and/or videos (&ldquo;Content&rdquo;) to Kogia&rsquo;s Oceanic
					Library, operated by Kogia, you (&ldquo;Contributor&rdquo;)
					acknowledge that you have read, understood, and agree to be bound by
					this Agreement.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						1. Purpose of Image and Video Donation
					</strong>
				</p>
				<p>
					Kogia&rsquo;s Oceanic Library is a nonprofit media library established
					to support conservation organizations, nonprofits, scientists,
					researchers, activists, and educators working to protect oceans and
					marine life.
				</p>
				<p>
					By contributing Content, you support Kogia&rsquo;s mission to make
					high-quality marine imagery and footage accessible exclusively for
					non-commercial, non-profit, conservation, educational, and advocacy
					purposes.
				</p>
				<p>
					<strong className="text-h5 font-bold">2. Ownership &amp; Rights</strong>
				</p>
				<p>
					<strong>2.1 Copyright Ownership</strong>
				</p>
				<p>
					You retain full copyright ownership of all Content you submit to
					Kogia&rsquo;s Oceanic Library. Nothing in this Agreement transfers
					ownership of your intellectual property to Kogia or to any third
					party.
				</p>
				<p>
					<strong>2.2 Moral Rights</strong>
				</p>
				<p>
					Kogia acknowledges and respects your moral rights, including the
					right of attribution and the right to object to misleading,
					derogatory, or inappropriate use of your Content.
				</p>
				<p>
					<strong className="text-h5 font-bold">3. License Granted to Kogia</strong>
				</p>
				<p>
					You grant Kogia a non-exclusive, worldwide, royalty-free, revocable
					license to:
				</p>
				<ul className="list-inside list-[auto]">
					<li>Host, store, archive, and display the Content</li>
					<li>Distribute the Content through the Oceanic Library</li>
					<li>
						Grant limited, non-commercial access to vetted library members in
						accordance with Kogia&rsquo;s <em>Terms of Service for Users</em>
					</li>
					<li>
						Use the Content for Kogia&rsquo;s own nonprofit communications,
						education, outreach, and fundraising activities
					</li>
				</ul>
				<p>
					This license is granted solely for purposes consistent with
					Kogia&rsquo;s nonprofit mission.
				</p>
				<p>
					<strong className="text-h5 font-bold">4. Licensing Framework</strong>
				</p>
				<p>
					Content made available through the Oceanic Library is distributed
					under a limited, revocable, non-exclusive, non-transferable,
					non-sublicensable, nonprofit-only content license, granted by Kogia to
					vetted members, with mandatory attribution and no commercial rights.
				</p>
				<p>
					Content made available through the Oceanic Library is distributed
					under a limited, revocable, non-exclusive, non-transferable,
					non-sublicensable, nonprofit-only license, administered by Kogia and
					granted to vetted members, with mandatory attribution and no
					commercial rights.
				</p>
				<p>
					This licensing framework is contractual and membership-based, and is
					not a public or open license.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						5. Permitted Uses by Library Members
					</strong>{' '}
					Your Content may be made available to vetted members for the following{' '}
					<strong>non-commercial uses only</strong>:
				</p>
				<ul className="list-inside list-[auto]">
					<li>Educational, conservation, and awareness videos</li>
					<li>Nonprofit fundraising materials</li>
					<li>Social Media for advocacy or education</li>
					<li>Websites and online publications for nonprofit initiatives</li>
					<li>Educational exhibitions</li>
					<li>Educational printed materials</li>
					<li>Awareness, conservation, or activist campaigns</li>
					<li>Small-scale, non-commercial art projects</li>
				</ul>
				<p>
					Commercial use is <strong>strictly prohibited</strong> unless
					separately negotiated directly with you, the Contributor.
				</p>
				<p>
					<strong className="text-h5 font-bold">5. Attribution &amp; Credit</strong>
				</p>
				<p>
					We greatly value your contribution and are committed to ensuring
					appropriate credit for your Content. All members are contractually
					required to credit both Kogia and the Contributor as a mandatory
					condition of use.
				</p>
				<p>
					<strong>6.1 Standard Attribution Format</strong>
				</p>
				<p>
					Unless otherwise requested by you in writing, attribution must include
					both Kogia and the Contributor, and will follow this format:
				</p>
				<p>
					<strong>Contributor Name / Kogia</strong>
				</p>
				<p>or</p>
				<p>
					<strong>Kogia / Contributor Name</strong>
				</p>
				<p>
					<strong>6.2 Platform-Specific Credit Guidelines</strong>
				</p>
				<p>
					Kogia&rsquo;s Member Terms of Service require users to credit
					Contributors as follows:
				</p>
				<ul className="list-inside list-[auto]">
					<li>
						<strong>Photos (Watermarked):</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Kogia&rsquo;s official watermarked versions constitute proper
								and sufficient attribution.
							</li>
							<li>
								Custom watermarks are acceptable if they clearly follow the
								standard attribution format.
							</li>
							<li>
								For social media use of watermarked images, the User must
								additionally:
								<ul className="ml-4 list-inside list-[auto]">
									<li>
										Tag Kogia&rsquo;s official account where technically
										possible, and
									</li>
									<li>Tag or clearly name the Contributor.</li>
								</ul>
							</li>
						</ul>
					</li>
					<li>
						<strong>Photos (Non-Watermarked):</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Attribution must appear in captions, credits, or adjacent to
								the image depending on the medium.
							</li>
							<li>
								On social media, users must credit in the caption or first
								comment and tag both Kogia and the Contributor where possible.
							</li>
						</ul>
					</li>
					<li>
						<strong>Video &amp; Film:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Contributors must be credited in end credits where credits are
								included.
							</li>
							<li>
								Where applicable, video descriptions must include:
								<br />
								<br />
								<em>
									Footage courtesy of Kogia&rsquo;s Oceanic Library &mdash;
									[Contributor Name]
								</em>
							</li>
							<li>
								On social media, users must tag both Kogia and the Contributor
								where platform functionality allows.
							</li>
						</ul>
					</li>
					<li>
						<strong>Educational Materials, Exhibitions &amp; Installations:</strong>
						<ul className="ml-4 list-inside list-[auto]">
							<li>
								Credits must appear on wall labels, captions, or accompanying
								materials where credits are customarily provided.
							</li>
						</ul>
					</li>
				</ul>
				<p>
					Kogia will take reasonable steps to address missing or improper
					attribution when identified. Furthermore, Attribution must remain
					intact for the entire duration of the media&rsquo;s use. Removal or
					alteration of credits after publication constitutes misuse.
				</p>
				<p>
					<strong className="text-h5 font-bold">7. Withdrawal of Content</strong>
				</p>
				<p>
					<strong>7.1 Right to Withdraw</strong>
				</p>
				<p>
					You may request removal of your Content from Kogia&rsquo;s Oceanic
					Library at any time by written notice to{' '}
					{/* eslint-disable-next-line remix-react-routes/use-link-for-routes -- mailto */}
					<a className="underline" href="mailto:info@kogia.org">
						info@kogia.org
					</a>{' '}
					or{' '}
					{/* eslint-disable-next-line remix-react-routes/use-link-for-routes -- mailto */}
					<a className="underline" href="mailto:nessim@kogia.org">
						nessim@kogia.org
					</a>
				</p>
				<p>
					<strong>7.2 Effect of Withdrawal</strong>
				</p>
				<p>Upon withdrawal:</p>
				<ul className="list-inside list-[auto]">
					<li>Kogia will remove the Content from the Oceanic Library</li>
					<li>Existing, lawful uses by members prior to removal may continue</li>
					<li>
						Withdrawal does not invalidate past uses made in compliance with
						the User Terms of Service
					</li>
				</ul>
				<p>
					This protects both Contributors and nonprofit users who relied on the
					license in good faith.
				</p>
				<p>
					<strong className="text-h5 font-bold">8. No Commercial Exploitation</strong>
				</p>
				<p>
					Kogia shall not sell, license, or commercially exploit your Content.
					Any commercial inquiries received by Kogia will be referred directly
					to you unless otherwise agreed in writing.
				</p>
				<p>
					<strong className="text-h5 font-bold">9. Termination</strong>
				</p>
				<p>
					Either party may terminate this Agreement at any time. Termination
					does not affect rights granted or uses made prior to termination in
					accordance with this Agreement.
				</p>
				<p>
					<strong className="text-h5 font-bold">10. Amendments</strong>
				</p>
				<p>
					Any amendments or modifications to this Agreement must be made in
					writing by Kogia. Continued participation after updates constitutes
					acceptance of revised terms.
				</p>
				<p>
					<strong className="text-h5 font-bold">
						11. Governing Law &amp; Jurisdiction
					</strong>
				</p>
				<p>
					This Agreement shall be governed by and construed in accordance with
					the laws of the United States of America. Any disputes arising under
					this Agreement shall be subject to the exclusive jurisdiction of the
					state or federal courts located in the United States, at
					Kogia&rsquo;s election.
				</p>
				<p>
					By submitting Content to Kogia&rsquo;s Oceanic Library, you
					acknowledge and agree to these terms.
				</p>
			</div>
		</div>
	)
}
